/**
 * 题目作答形态（interaction axis）
 * 材料轴见 QuestionStimulus，勿再把 listening/reading 塞进本枚举。
 */
export enum QuestionType {
  /** 单选题 */
  SINGLE_CHOICE = 'single_choice',
  /** 多选题 */
  MULTIPLE_CHOICE = 'multiple_choice',
  /** 判断题 */
  TRUE_FALSE = 'true_false',
  /** 填空题（短文本；开放写作/口语暂挂此型 + responseMode） */
  FILL_BLANK = 'fill_blank',
}

/**
 * 题目材料刺激（stimulus axis），可组合。
 */
export type QuestionStimulus = 'audio' | 'passage' | 'image';

export const QUESTION_STIMULUS_VALUES: readonly QuestionStimulus[] = [
  'audio',
  'passage',
  'image',
] as const;

/**
 * 作答封闭性（考试特化定义用；题目 JSON 可不强制携带）
 */
export type QuestionResponseMode = 'closed' | 'open_ended';

/**
 * 选项接口
 */
export interface Option {
  id: string;
  content: string;
  isCorrect?: boolean;
}

/**
 * 题目接口
 */
export interface Question {
  id: string;
  type: QuestionType;
  content: string;
  options?: Option[];
  correctAnswer: string | string[];
  explanation?: string;
  level?: 'A1' | 'A2' | 'B1' | 'B2';
  tags?: string[];
  /**
   * 受控内容主题 id（产线词表，见 exams topicCatalog）。
   * 与 tags（知识点自由标签）分离；不是 Platform Scope。
   */
  topics?: string[];
  /** 开放作答（写作/口语）；缺省按题型/校验推断 */
  responseMode?: QuestionResponseMode;
  /** 写作任务号等考试特化字段（AI 生产须存活到 OSS） */
  taskNumber?: number;
  minWordCount?: number;
  maxWordCount?: number;
  task?: number | string;
  taskType?: string;
  /** 材料轴；无材料时可省略或 [] */
  stimulus?: QuestionStimulus[];
  /** 音频文件路径（stimulus 含 audio 时） */
  audioUrl?: string;
  /** 音频对象名；用于在短期 URL 失效后重新签名。 */
  audioObjectName?: string;
  /** 音频生成状态：pending=待生成, failed=生成失败 */
  audioStatus?: 'pending' | 'failed';
  /** 阅读材料（stimulus 含 passage 时） */
  passage?: string;
  /**
   * 共享材料集 id（Content Inventory `passageSets[]`）。
   * 同文多题共用；题面仍可 denormalize `passage`。
   */
  passageSetId?: string;
  /** 套卷文章编号（IELTS 1–3 等）；与 passageSet 成对 */
  passageNumber?: number;
  /** 考试特化子题型标签（IELTS/TOEFL questionFormat 等） */
  questionFormat?: string;
  /** 材料类型标签（TCF materialType 等） */
  materialType?: string;
  /** 图片URL（stimulus 含 image 或可选配图） */
  imageUrl?: string;
  /** 考试部分 / Skill id（如 listening, reading） */
  section?: string;
  /** 排序顺序 */
  sortOrder?: number;
}

/**
 * 用户答题记录
 */
export interface UserAnswer {
  questionId: string;
  answer: string | string[];
  isCorrect: boolean;
  timeSpent: number; // 毫秒
  timestamp: number;
}

/**
 * 考试配置
 */
export interface ExamConfig {
  id: string;
  name: string;
  /** 考试时长（分钟） */
  duration: number;
  /** 题目列表 */
  questions: Question[];
  /** 是否随机顺序 */
  shuffle?: boolean;
  /** 是否显示答案解析 */
  showExplanation?: boolean;
}

/**
 * 考试会话
 */
export interface ExamSession {
  id: string;
  configId: string;
  startTime: number;
  endTime?: number;
  answers: UserAnswer[];
  status: 'in_progress' | 'completed' | 'paused';
  score?: number;
}

/**
 * 错题记录
 */
export interface MistakeRecord {
  questionId: string;
  question: Question;
  userAnswer: string | string[];
  wrongCount: number;
  lastWrongTime: number;
  reviewCount: number;
  nextReviewTime?: number;
}

/**
 * 考试特定的题目类型定义
 * 每个考试可以为同一基础类型定义不同的约束
 */
export interface ExamSpecificQuestionTypeDefinition {
  /** 全局唯一标识：{examId}_{skill} 如 'ielts_listening' */
  id: string;

  /** 基础作答形态 */
  baseType: QuestionType;

  /** 显示名称 */
  name: string;

  /** 该考试题型要求的材料轴（生成/校验写入 Question.stimulus） */
  requiredStimulus?: QuestionStimulus[];

  /** 封闭作答 vs 开放作答（写作/口语挂 fill_blank 时标 open_ended） */
  responseMode?: QuestionResponseMode;

  /** 该考试类型特有的字段定义（扩展或覆盖基础定义） */
  fieldOverrides?: Partial<Record<string, FieldDefinition>>;

  /** 额外字段（仅该考试类型使用） */
  extraFields?: Record<string, FieldDefinition>;

  /** 渲染配置覆盖 */
  renderConfigOverride?: Partial<RenderConfig>;

  /** 自定义验证器（在基础验证之后执行） */
  customValidator?: (question: any) => ValidationError[];

  /** 该类型特有的约束规则 */
  constraints?: Record<string, any>;
}

/**
 * 字段定义（从 questionTypes 类型导入）
 */
export interface FieldDefinition {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'array' | 'object' | 'url' | 'text';
  required: boolean;
  description?: string;
  validator?: (value: any) => boolean | string;
  defaultValue?: any;
}

/**
 * 验证错误
 */
export interface ValidationError {
  field: string;
  message: string;
  code?: string;
  /**
   * blocking = 不可导入；warning = 可导入但需人工留意。
   * 缺省按 blocking 处理（向后兼容）。
   */
  severity?: 'blocking' | 'warning';
}

/**
 * 渲染配置
 */
export interface RenderConfig {
  /** 布局类型 */
  layout?: 'default' | 'audio-first' | 'passage-first' | 'image-first';
  /** 需要显示的字段顺序 */
  showFields?: string[];
  /** 自定义组件名称（前端使用） */
  component?: string;
  /** 字段显示配置 */
  fieldConfig?: Record<
    string,
    {
      label?: string;
      placeholder?: string;
      component?: string;
    }
  >;
}

/**
 * Section 级别的题目类型约束
 */
export interface SectionQuestionTypeConstraint {
  /** 引用的题目类型定义ID */
  questionTypeId: string;

  /** 该 Section 中此类型的题目数量限制（可选；套卷/模拟卷模板用，AI 生题题量由调用方 count 传参，勿写死在 Skill） */
  countLimit?: { min: number; max: number };

  /** Section特定的字段约束 */
  fieldConstraints?: Record<
    string,
    {
      required?: boolean;
      allowedValues?: any[];
      min?: number;
      max?: number;
    }
  >;

  /** Section特定的渲染覆盖 */
  renderOverride?: Partial<RenderConfig>;
}

/**
 * 考试类型配置（由具体考试包提供）
 * 增强版：支持考试特定的题目类型定义
 */
export interface ExamTypeConfig {
  id: string;
  name: string;
  description: string;

  /**
   * Owning Language id from EXAM_STRUCTURE (e.g. 'french', 'english').
   * Required so Content Inventory / entitlement scopes can resolve without a separate lookup.
   */
  languageId: string;

  /**
   * 该考试定义的题目类型
   * 键为类型ID，如 'ielts_listening'
   */
  questionTypeDefinitions?: Record<string, ExamSpecificQuestionTypeDefinition>;

  /** 考试部分 */
  sections: ExamSection[];

  /** 评分规则 */
  scoringRules: ScoringRules;
}

/**
 * 考试分区（如听力、阅读等）
 * 增强版：支持 Section 级别的题目类型约束
 */
export interface ExamSection {
  id: string;
  name: string;
  duration: number;

  /**
   * @deprecated 使用 questionTypeConstraints 替代
   * 支持的题目类型（向后兼容）
   */
  questionTypes?: QuestionType[];

  /**
   * 支持的题目类型约束
   * 替代原来的简单 QuestionType 数组
   */
  questionTypeConstraints?: SectionQuestionTypeConstraint[];

  /** 可选：该部分官方模考题量；未设置时 buildPaper 仅按库存上限抽样，不视为全真卷 */
  questionCount?: number;

  /**
   * Whether this Skill enters the platform full-mock paper.
   * Default true. Optional tracks (e.g. TCF Expression écrite) set false so they
   * remain trainable in Exam Space without inflating mandatory mock modules.
   */
  includeInFullMock?: boolean;
}

/**
 * 评分规则
 */
export interface ScoringRules {
  totalScore: number;
  passingScore: number;
  /** 每题分值或自定义评分函数 */
  calculateScore: (answers: UserAnswer[], questions: Question[]) => number;
}

/**
 * 试卷中的一部分（按 section 分组）
 */
export interface PaperSection {
  sectionId: string;
  sectionName: string;
  durationMinutes: number;
  questionTypes: QuestionType[];
  /**
   * 使用的题目类型约束ID
   */
  questionTypeConstraintIds?: string[];
  /** 该 section 内题目，顺序固定 */
  questions: Question[];
}

/**
 * 试卷（模拟考试用）
 */
export interface Paper {
  id: string;
  examTypeId: string;
  /** 按 section 分组的题目列表，顺序即考试顺序 */
  sections: PaperSection[];
  /** 总时长（分钟），来自 config.sections 之和 */
  totalDurationMinutes: number;
  createdAt: number;
}

/**
 * 模拟考试：按 section 分块的答题卡数据项
 */
export interface AnswerSheetSectionItem {
  index: number;
  answered: boolean;
  marked: boolean;
}

/**
 * 模拟考试：按 section 分块的答题卡
 */
export interface AnswerSheetBySection {
  sections: {
    sectionId: string;
    sectionName: string;
    questionTypes: QuestionType[];
    /** 题目在全局 questions 中的下标 */
    indices: number[];
    /** 每道题的答题状态 */
    items: AnswerSheetSectionItem[];
  }[];
}
